import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { ArrowLeft, Building2, Loader2, PlusCircle, CheckCircle, XCircle } from 'lucide-react';
import type { User } from '../App';
import apiClient from '../api/auth';
import { getSlotRequests, approveSlotRequest, rejectSlotRequest, type SlotRequestRow } from '../api/institutions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';

interface ApplicationRow {
  institution_uuid: string;
  institution_name: string;
  short_name?: string;
  contact_person: { name: string; email: string; title?: string };
  requested_slots: number;
  application_status: string;
  submitted_at: string;
  reference_number: string;
}

interface ApplicationsResponse {
  applications: ApplicationRow[];
  pagination: { page: number; limit: number; status: string };
  summary: { total_pending: number; total_approved: number; total_rejected: number };
}

export function SuperAdminInstitutionsPage({ user, onBack }: { user: User; onBack: () => void }) {
  const [data, setData] = useState<ApplicationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('pending_review');
  const [addSlotsOpen, setAddSlotsOpen] = useState(false);
  const [addSlotsRow, setAddSlotsRow] = useState<ApplicationRow | null>(null);
  const [slotsToAdd, setSlotsToAdd] = useState('100');
  const [addSlotsNotes, setAddSlotsNotes] = useState('');
  const [addSlotsSubmitting, setAddSlotsSubmitting] = useState(false);
  const [addSlotsError, setAddSlotsError] = useState<string | null>(null);

  const [slotRequests, setSlotRequests] = useState<SlotRequestRow[]>([]);
  const [slotRequestsLoading, setSlotRequestsLoading] = useState(false);
  const [slotRequestStatusFilter, setSlotRequestStatusFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [approveRequestRow, setApproveRequestRow] = useState<SlotRequestRow | null>(null);
  const [approveSlotsGranted, setApproveSlotsGranted] = useState('');
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [rejectRequestRow, setRejectRequestRow] = useState<SlotRequestRow | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const fetchApplications = () => {
    setLoading(true);
    apiClient.get<ApplicationsResponse>('/super-admin/institutions/applications', { params: { status: statusFilter, limit: 50 } })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient
      .get<ApplicationsResponse>('/super-admin/institutions/applications', { params: { status: statusFilter, limit: 50 } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [statusFilter]);

  useEffect(() => {
    let cancelled = false;
    setSlotRequestsLoading(true);
    getSlotRequests(slotRequestStatusFilter)
      .then((r) => { if (!cancelled) setSlotRequests(r.requests); })
      .catch(() => { if (!cancelled) setSlotRequests([]); })
      .finally(() => { if (!cancelled) setSlotRequestsLoading(false); });
    return () => { cancelled = true; };
  }, [slotRequestStatusFilter]);

  const openApproveSlotRequest = (row: SlotRequestRow) => {
    setApproveRequestRow(row);
    setApproveSlotsGranted(String(row.requested_slots));
  };
  const submitApproveSlotRequest = async () => {
    if (!approveRequestRow) return;
    const num = parseInt(approveSlotsGranted, 10);
    if (isNaN(num) || num < 1) return;
    setApproveSubmitting(true);
    try {
      await approveSlotRequest(approveRequestRow.request_uuid, num);
      setApproveRequestRow(null);
      getSlotRequests(slotRequestStatusFilter).then((r) => setSlotRequests(r.requests));
    } finally {
      setApproveSubmitting(false);
    }
  };

  const openRejectSlotRequest = (row: SlotRequestRow) => {
    setRejectRequestRow(row);
    setRejectReason('');
  };
  const submitRejectSlotRequest = async () => {
    if (!rejectRequestRow) return;
    setRejectSubmitting(true);
    try {
      await rejectSlotRequest(rejectRequestRow.request_uuid, rejectReason.trim() || undefined);
      setRejectRequestRow(null);
      getSlotRequests(slotRequestStatusFilter).then((r) => setSlotRequests(r.requests));
    } finally {
      setRejectSubmitting(false);
    }
  };

  const openAddSlots = (row: ApplicationRow) => {
    if (row.application_status !== 'approved') return;
    setAddSlotsRow(row);
    setSlotsToAdd('100');
    setAddSlotsNotes('');
    setAddSlotsError(null);
    setAddSlotsOpen(true);
  };

  const submitAddSlots = async () => {
    if (!addSlotsRow) return;
    const num = parseInt(slotsToAdd, 10);
    if (isNaN(num) || num < 1) {
      setAddSlotsError('Enter a valid number of slots (at least 1).');
      return;
    }
    setAddSlotsSubmitting(true);
    setAddSlotsError(null);
    try {
      await apiClient.post(`/super-admin/institutions/${addSlotsRow.institution_uuid}/add-slots`, {
        slots_to_add: num,
        notes: addSlotsNotes.trim() || undefined,
      });
      setAddSlotsOpen(false);
      setAddSlotsRow(null);
      fetchApplications();
    } catch (e: any) {
      setAddSlotsError(e?.response?.data?.message || e?.message || 'Failed to add slots');
    } finally {
      setAddSlotsSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to dashboard
        </Button>
        <div className="flex items-center gap-2">
          <Building2 className="w-6 h-6 text-[var(--gold)]" />
          <h1 className="text-xl font-display font-semibold text-[var(--navy)]">Institution applications</h1>
        </div>
      </div>

      {data && (
        <div className="flex gap-2 flex-wrap">
          <Badge variant={statusFilter === 'pending_review' ? 'default' : 'secondary'}>
            Pending: {data.summary.total_pending}
          </Badge>
          <Badge variant="secondary">Approved: {data.summary.total_approved}</Badge>
          <Badge variant="secondary">Rejected: {data.summary.total_rejected}</Badge>
        </div>
      )}

      <div className="flex gap-2">
        {['pending_review', 'approved', 'rejected', 'all'].map((s) => (
          <Button
            key={s}
            variant={statusFilter === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(s)}
            className={statusFilter === s ? 'bg-[var(--gold)] text-[var(--navy)]' : ''}
          >
            {s === 'all' ? 'All' : s.replace('_', ' ')}
          </Button>
        ))}
      </div>

      <Card className="border-[rgba(201,168,76,0.25)]">
        <CardHeader>
          <CardTitle>Applications</CardTitle>
          <CardDescription>Review and approve or reject institution access requests</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[var(--gold)]" />
            </div>
          ) : data && data.applications.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Institution</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Requested slots</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.applications.map((row) => (
                  <TableRow key={row.institution_uuid}>
                    <TableCell className="font-mono text-sm">{row.reference_number}</TableCell>
                    <TableCell>{row.institution_name} {row.short_name ? `(${row.short_name})` : ''}</TableCell>
                    <TableCell>{row.contact_person?.name} — {row.contact_person?.email}</TableCell>
                    <TableCell>{row.requested_slots}</TableCell>
                    <TableCell>{new Date(row.submitted_at).toLocaleDateString()}</TableCell>
                    <TableCell><Badge variant="secondary">{row.application_status}</Badge></TableCell>
                    <TableCell className="text-right">
                      {row.application_status === 'approved' && (
                        <Button size="sm" variant="outline" onClick={() => openAddSlots(row)} className="text-[var(--gold)] border-[var(--gold)]">
                          <PlusCircle className="w-4 h-4 mr-1" />
                          Add slots
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-[var(--navy)]/70 py-6 text-center">No applications match the selected filter.</p>
          )}
        </CardContent>
      </Card>

      <Dialog open={addSlotsOpen} onOpenChange={(open) => { setAddSlotsOpen(open); if (!open) setAddSlotsRow(null); setAddSlotsError(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add slots</DialogTitle>
            <DialogDescription>
              {addSlotsRow ? `${addSlotsRow.institution_name} — add more upload slots (renewal).` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="slots-to-add">Slots to add</Label>
              <Input
                id="slots-to-add"
                type="number"
                min={1}
                value={slotsToAdd}
                onChange={(e) => setSlotsToAdd(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="add-slots-notes">Notes (optional)</Label>
              <Textarea
                id="add-slots-notes"
                placeholder="e.g. Contract renewal Q2 2026"
                value={addSlotsNotes}
                onChange={(e) => setAddSlotsNotes(e.target.value)}
                rows={2}
              />
            </div>
            {addSlotsError && <p className="text-sm text-red-600">{addSlotsError}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddSlotsOpen(false)} disabled={addSlotsSubmitting}>Cancel</Button>
            <Button onClick={submitAddSlots} disabled={addSlotsSubmitting} className="bg-[var(--gold)] text-[var(--navy)]">
              {addSlotsSubmitting ? 'Adding...' : 'Add slots'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card className="border-[rgba(201,168,76,0.25)]">
        <CardHeader>
          <CardTitle>Slot requests from institutions</CardTitle>
          <CardDescription>Institutional admins can request more slots; approve or reject here.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            {(['pending', 'approved', 'rejected', 'all'] as const).map((s) => (
              <Button
                key={s}
                variant={slotRequestStatusFilter === s ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSlotRequestStatusFilter(s)}
                className={slotRequestStatusFilter === s ? 'bg-[var(--gold)] text-[var(--navy)]' : ''}
              >
                {s}
              </Button>
            ))}
          </div>
          {slotRequestsLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[var(--gold)]" /></div>
          ) : slotRequests.length === 0 ? (
            <p className="text-[var(--navy)]/70 py-6 text-center">No slot requests match the filter.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Institution</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead>Requested at</TableHead>
                  <TableHead>Status</TableHead>
                  {slotRequestStatusFilter === 'pending' && <TableHead className="text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {slotRequests.map((row) => (
                  <TableRow key={row.request_uuid}>
                    <TableCell>{row.institution_name} {row.short_name ? `(${row.short_name})` : ''}</TableCell>
                    <TableCell>{row.requested_slots}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{row.notes || '—'}</TableCell>
                    <TableCell>{new Date(row.requested_at).toLocaleString()}</TableCell>
                    <TableCell><Badge variant="secondary">{row.status}</Badge></TableCell>
                    {slotRequestStatusFilter === 'pending' && (
                      <TableCell className="text-right">
                        <Button size="sm" className="mr-2 bg-green-600 hover:bg-green-700" onClick={() => openApproveSlotRequest(row)}>
                          <CheckCircle className="w-4 h-4 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => openRejectSlotRequest(row)}>
                          <XCircle className="w-4 h-4 mr-1" /> Reject
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!approveRequestRow} onOpenChange={(open) => { if (!open) setApproveRequestRow(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Approve slot request</DialogTitle>
            <DialogDescription>
              {approveRequestRow ? `${approveRequestRow.institution_name} requested ${approveRequestRow.requested_slots} slots. Grant amount (default = requested):` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="approve-slots-granted">Slots to grant</Label>
            <Input
              id="approve-slots-granted"
              type="number"
              min={1}
              value={approveSlotsGranted}
              onChange={(e) => setApproveSlotsGranted(e.target.value)}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveRequestRow(null)} disabled={approveSubmitting}>Cancel</Button>
            <Button onClick={submitApproveSlotRequest} disabled={approveSubmitting} className="bg-[var(--gold)] text-[var(--navy)]">
              {approveSubmitting ? 'Approving...' : 'Approve & add slots'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!rejectRequestRow} onOpenChange={(open) => { if (!open) setRejectRequestRow(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reject slot request</DialogTitle>
            <DialogDescription>
              {rejectRequestRow ? `Reject ${rejectRequestRow.institution_name}'s request for ${rejectRequestRow.requested_slots} slots? Optional reason:` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="reject-reason">Reason (optional)</Label>
            <Textarea
              id="reject-reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="e.g. Contract not renewed"
              rows={2}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectRequestRow(null)} disabled={rejectSubmitting}>Cancel</Button>
            <Button variant="destructive" onClick={submitRejectSlotRequest} disabled={rejectSubmitting}>
              {rejectSubmitting ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
